import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  DollarSign, Plus, CreditCard, CheckCircle, Clock, AlertCircle, AlertTriangle, Baby, School, Users, FileText, Info, Layers, Receipt, Search, Pencil, Trash2, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import BillingWorksheet from '@/components/BillingWorksheet';
import AutoInvoiceGenerator from '@/components/AutoInvoiceGenerator';

import API from '@/config/api';
// API imported from config;

// Helper to calculate age in months from DOB
const getAgeInMonths = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  const today = new Date();
  const months = (today.getFullYear() - dob.getFullYear()) * 12 + (today.getMonth() - dob.getMonth());
  return months;
};

// Helper to determine age group from DOB
const getAgeGroup = (dateOfBirth) => {
  const months = getAgeInMonths(dateOfBirth);
  if (months === null) return null;
  
  if (months <= 12) return 'infants';
  if (months <= 36) return 'toddlers'; // 1-3 years
  if (months <= 60) return 'preschool'; // 3-5 years
  return 'school_age'; // 6+
};

// Age group display names
const AGE_GROUP_LABELS = {
  infants: { label: 'Infants (0-12 months)', icon: Baby, color: 'text-pink-500' },
  toddlers: { label: 'Toddlers (1-3 years)', icon: Baby, color: 'text-blue-500' },
  preschool: { label: 'Preschool (3-5 years)', icon: School, color: 'text-green-500' },
  school_age: { label: 'School Age (6+ years)', icon: Users, color: 'text-purple-500' }
};

const BillingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [children, setChildren] = useState([]);
  const [parents, setParents] = useState([]);
  const [feesSettings, setFeesSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Invoice search and edit state
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [editInvoiceDialog, setEditInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [savingInvoice, setSavingInvoice] = useState(false);
  
  const [formData, setFormData] = useState({
    parent_id: '',
    child_id: '',
    invoice_type: 'tuition', // 'tuition' or 'registration'
    amount: '',
    description: '',
    due_date: ''
  });
  const [selectedChildInfo, setSelectedChildInfo] = useState(null);
  
  // Bulk invoice state
  const [bulkForm, setBulkForm] = useState({
    invoice_type: 'tuition', // 'tuition', 'registration', 'both'
    due_date: '',
    selected_children: [], // empty = all children
    include_all: true
  });
  const [bulkPreview, setBulkPreview] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/invoices`, getAuthHeaders()),
        axios.get(`${API}/settings/fees`, getAuthHeaders()).catch(() => ({ data: {} }))
      ];
      
      if (user.role !== 'parent') {
        requests.push(axios.get(`${API}/children`, getAuthHeaders()));
        requests.push(axios.get(`${API}/users?role=parent`, getAuthHeaders()));
      }

      const responses = await Promise.all(requests);
      setInvoices(responses[0].data);
      setFeesSettings(responses[1].data);
      
      if (responses[2]) setChildren(responses[2].data);
      if (responses[3]) setParents(responses[3].data);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Handle child selection - auto-populate fee based on age group
  const handleChildSelect = (childId) => {
    const child = children.find(c => c.id === childId);
    if (!child) {
      setFormData(prev => ({ ...prev, child_id: childId }));
      setSelectedChildInfo(null);
      return;
    }

    const ageGroup = getAgeGroup(child.date_of_birth);
    const ageGroupInfo = AGE_GROUP_LABELS[ageGroup] || null;
    
    setSelectedChildInfo({
      child,
      ageGroup,
      ageGroupInfo
    });

    // Auto-populate amount based on invoice type
    let amount = '';
    let description = '';
    
    if (formData.invoice_type === 'tuition' && feesSettings && feesSettings[ageGroup]) {
      const fee = feesSettings[ageGroup];
      amount = fee.amount || '';
      const rateType = fee.rate_type === 'monthly' ? 'Monthly' : 'Weekly';
      description = `${rateType} Tuition - ${child.first_name} ${child.last_name} (${ageGroupInfo?.label || ageGroup})`;
    } else if (formData.invoice_type === 'registration' && feesSettings?.registration) {
      amount = feesSettings.registration.amount || '';
      description = `Registration Fee - ${child.first_name} ${child.last_name}`;
    }

    setFormData(prev => ({
      ...prev,
      child_id: childId,
      parent_id: child.parent_id || prev.parent_id,
      amount,
      description
    }));
  };

  // Handle invoice type change
  const handleInvoiceTypeChange = (type) => {
    setFormData(prev => ({ ...prev, invoice_type: type }));
    
    // Re-calculate amount if child is selected
    if (formData.child_id && selectedChildInfo) {
      const { child, ageGroup, ageGroupInfo } = selectedChildInfo;
      let amount = '';
      let description = '';
      
      if (type === 'tuition' && feesSettings && feesSettings[ageGroup]) {
        const fee = feesSettings[ageGroup];
        amount = fee.amount || '';
        const rateType = fee.rate_type === 'monthly' ? 'Monthly' : 'Weekly';
        description = `${rateType} Tuition - ${child.first_name} ${child.last_name} (${ageGroupInfo?.label || ageGroup})`;
      } else if (type === 'registration' && feesSettings?.registration) {
        amount = feesSettings.registration.amount || '';
        description = `Registration Fee - ${child.first_name} ${child.last_name}`;
      }
      
      setFormData(prev => ({ ...prev, amount, description }));
    }
  };

  // Generate bulk invoice preview
  const generateBulkPreview = () => {
    if (!feesSettings) return [];
    
    const targetChildren = bulkForm.include_all 
      ? children 
      : children.filter(c => bulkForm.selected_children.includes(c.id));
    
    const preview = [];
    
    targetChildren.forEach(child => {
      const ageGroup = getAgeGroup(child.date_of_birth);
      const ageGroupInfo = AGE_GROUP_LABELS[ageGroup];
      
      // Add tuition invoice
      if ((bulkForm.invoice_type === 'tuition' || bulkForm.invoice_type === 'both') && feesSettings[ageGroup]) {
        const fee = feesSettings[ageGroup];
        const rateType = fee.rate_type === 'monthly' ? 'Monthly' : 'Weekly';
        preview.push({
          child_id: child.id,
          child_name: `${child.first_name} ${child.last_name}`,
          parent_id: child.parent_id,
          type: 'tuition',
          age_group: ageGroupInfo?.label || ageGroup,
          amount: parseFloat(fee.amount) || 0,
          description: `${rateType} Tuition - ${child.first_name} ${child.last_name} (${ageGroupInfo?.label || ageGroup})`
        });
      }
      
      // Add registration fee
      if ((bulkForm.invoice_type === 'registration' || bulkForm.invoice_type === 'both') && feesSettings.registration) {
        preview.push({
          child_id: child.id,
          child_name: `${child.first_name} ${child.last_name}`,
          parent_id: child.parent_id,
          type: 'registration',
          age_group: ageGroupInfo?.label || ageGroup,
          amount: parseFloat(feesSettings.registration.amount) || 0,
          description: `Registration Fee - ${child.first_name} ${child.last_name}`
        });
      }
    });
    
    return preview;
  };

  // Update preview when bulk form changes
  useEffect(() => {
    if (bulkDialogOpen) {
      setBulkPreview(generateBulkPreview());
    }
  }, [bulkForm, bulkDialogOpen, children, feesSettings]);

  // Handle bulk invoice creation
  const handleBulkInvoice = async () => {
    if (!bulkForm.due_date) {
      toast.error('Please select a due date');
      return;
    }
    
    if (bulkPreview.length === 0) {
      toast.error('No invoices to create');
      return;
    }
    
    setBulkProcessing(true);
    
    try {
      const invoices = bulkPreview.map(inv => ({
        parent_id: inv.parent_id,
        child_id: inv.child_id,
        amount: inv.amount,
        description: inv.description,
        due_date: bulkForm.due_date,
        invoice_type: inv.type
      }));
      
      await axios.post(`${API}/invoices/bulk`, { invoices }, getAuthHeaders());
      
      toast.success(`Successfully created ${invoices.length} invoices`);
      setBulkDialogOpen(false);
      setBulkForm({ invoice_type: 'tuition', due_date: '', selected_children: [], include_all: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bulk invoices');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Toggle child selection for bulk
  const toggleChildSelection = (childId) => {
    setBulkForm(prev => ({
      ...prev,
      selected_children: prev.selected_children.includes(childId)
        ? prev.selected_children.filter(id => id !== childId)
        : [...prev.selected_children, childId]
    }));
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/invoices`, {
        parent_id: formData.parent_id,
        child_id: formData.child_id,
        amount: parseFloat(formData.amount),
        description: formData.description,
        due_date: formData.due_date,
        invoice_type: formData.invoice_type
      }, getAuthHeaders());
      
      toast.success('Invoice created successfully');
      setDialogOpen(false);
      setFormData({ parent_id: '', child_id: '', invoice_type: 'tuition', amount: '', description: '', due_date: '' });
      setSelectedChildInfo(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handlePayment = async (invoice) => {
    setProcessing(invoice.id);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-checkout?invoice_id=${invoice.id}&origin_url=${encodeURIComponent(originUrl)}`,
        {},
        getAuthHeaders()
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      partial: 'bg-blue-100 text-blue-800'
    };
    
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      paid: <CheckCircle className="w-3 h-3" />,
      overdue: <AlertCircle className="w-3 h-3" />,
      partial: <DollarSign className="w-3 h-3" />
    };
    
    return (
      <span className={cn("px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1", styles[status] || styles.pending)}>
        {icons[status] || icons.pending}
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </span>
    );
  };

  // Invoice editing handlers
  const openEditInvoice = (invoice) => {
    setEditingInvoice({
      ...invoice,
      amount: invoice.amount?.toString() || '',
      amount_paid: invoice.amount_paid?.toString() || '0'
    });
    setEditInvoiceDialog(true);
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    
    setSavingInvoice(true);
    try {
      await axios.put(
        `${API}/invoices/${editingInvoice.id}`,
        {
          description: editingInvoice.description,
          amount: parseFloat(editingInvoice.amount),
          amount_paid: parseFloat(editingInvoice.amount_paid) || 0,
          due_date: editingInvoice.due_date,
          status: editingInvoice.status
        },
        getAuthHeaders()
      );
      
      toast.success('Invoice updated successfully');
      setEditInvoiceDialog(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, getAuthHeaders());
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  // Filter invoices based on search and status
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoiceSearch === '' || 
      invoice.description?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      invoice.child_name?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      invoice.parent_email?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      invoice.id?.toLowerCase().includes(invoiceSearch.toLowerCase());
    
    const matchesStatus = invoiceStatusFilter === 'all' || invoice.status === invoiceStatusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <DashboardLayout title="Billing & Payments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pendingInvoices = invoices.filter(i => i.status === 'pending');
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const overdueInvoices = invoices.filter(i => i.status === 'overdue');
  const lateFeeInvoices = invoices.filter(i => i.invoice_type === 'late_fee');
  const totalPending = pendingInvoices.reduce((sum, i) => sum + i.amount, 0);
  const totalPaid = paidInvoices.reduce((sum, i) => sum + (i.amount_paid || i.amount || 0), 0);
  const totalOverdue = overdueInvoices.reduce((sum, i) => sum + i.amount, 0);
  const totalLateFees = lateFeeInvoices.reduce((sum, i) => sum + i.amount, 0);
  const paidLateFees = lateFeeInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount_paid || i.amount || 0), 0);

  return (
    <DashboardLayout title="Billing & Payments">
      <div className="space-y-6" data-testid="billing-page">
        {/* Tabs for Invoices and Worksheet */}
        <Tabs defaultValue="invoices" className="space-y-6">
          <TabsList className="bg-muted p-1 rounded-xl">
            <TabsTrigger value="invoices" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Receipt className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
            {user.role !== 'parent' && (
              <TabsTrigger value="worksheet" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <FileText className="w-4 h-4 mr-2" />
                Billing Worksheet
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="invoices" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid md:grid-cols-5 gap-4">
              <Card className="card-clay">
                <CardContent className="p-4">
                  <div className="w-12 h-12 bg-green-100 text-green-600 rounded-xl flex items-center justify-center mb-3">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <p className="text-2xl font-bold text-green-700" data-testid="total-paid-amount">${totalPaid.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p className="text-sm text-muted-foreground">Total Paid ({paidInvoices.length} invoices)</p>
                </CardContent>
              </Card>
              <Card className="card-clay">
                <CardContent className="p-4">
                  <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center mb-3">
                    <Clock className="w-6 h-6" />
                  </div>
                  <p className="text-2xl font-bold text-amber-600" data-testid="total-pending-amount">${totalPending.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p className="text-sm text-muted-foreground">Pending ({pendingInvoices.length} invoices)</p>
                </CardContent>
              </Card>
              <Card className="card-clay">
                <CardContent className="p-4">
                  <div className="w-12 h-12 bg-red-100 text-red-600 rounded-xl flex items-center justify-center mb-3">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <p className="text-2xl font-bold text-red-600" data-testid="total-overdue-amount">${totalOverdue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p className="text-sm text-muted-foreground">Overdue ({overdueInvoices.length} invoices)</p>
                </CardContent>
              </Card>
              <Card className="card-clay">
                <CardContent className="p-4">
                  <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mb-3">
                    <Receipt className="w-6 h-6" />
                  </div>
                  <p className="text-2xl font-bold text-purple-700" data-testid="total-late-fees">${totalLateFees.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p className="text-sm text-muted-foreground">Late Fees ({lateFeeInvoices.length} assessed{paidLateFees > 0 ? `, $${paidLateFees.toFixed(2)} collected` : ''})</p>
                </CardContent>
              </Card>
              <Card className="card-clay">
                <CardContent className="p-4">
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-3">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <p className="text-2xl font-bold">{invoices.length}</p>
                  <p className="text-sm text-muted-foreground">Total Invoices</p>
                </CardContent>
              </Card>
            </div>

        {/* Create Invoice Buttons (Admin/Provider only) */}
        {user.role !== 'parent' && (
          <div className="flex justify-end gap-3">
            {/* Auto Invoice Generator */}
            <AutoInvoiceGenerator onInvoicesCreated={fetchData} />

            {/* Bulk Invoice Dialog */}
            <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-invoice-btn">
                  <Layers className="w-4 h-4 mr-2" />
                  Bulk Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Create Bulk Invoices
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Invoice Type Selection */}
                  <div className="space-y-2">
                    <Label>What to Invoice</Label>
                    <Select 
                      value={bulkForm.invoice_type} 
                      onValueChange={(v) => setBulkForm(prev => ({ ...prev, invoice_type: v }))}
                    >
                      <SelectTrigger data-testid="bulk-invoice-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tuition">
                          <span className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-500" />
                            Tuition Only
                          </span>
                        </SelectItem>
                        <SelectItem value="registration">
                          <span className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-amber-500" />
                            Registration Fee Only
                          </span>
                        </SelectItem>
                        <SelectItem value="both">
                          <span className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-blue-500" />
                            Both Tuition + Registration
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Due Date */}
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={bulkForm.due_date}
                      onChange={(e) => setBulkForm(prev => ({ ...prev, due_date: e.target.value }))}
                      required
                      data-testid="bulk-due-date"
                    />
                  </div>

                  {/* Children Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Children to Invoice</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="include-all"
                          checked={bulkForm.include_all}
                          onCheckedChange={(checked) => setBulkForm(prev => ({ 
                            ...prev, 
                            include_all: checked,
                            selected_children: checked ? [] : prev.selected_children
                          }))}
                        />
                        <Label htmlFor="include-all" className="text-sm cursor-pointer">All Children ({children.length})</Label>
                      </div>
                    </div>
                    
                    {!bulkForm.include_all && (
                      <ScrollArea className="h-40 border rounded-lg p-2">
                        <div className="space-y-2">
                          {children.map(child => {
                            const ageGroup = getAgeGroup(child.date_of_birth);
                            const ageInfo = AGE_GROUP_LABELS[ageGroup];
                            return (
                              <div 
                                key={child.id} 
                                className={cn(
                                  "flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-muted/50",
                                  bulkForm.selected_children.includes(child.id) && "bg-primary/10"
                                )}
                                onClick={() => toggleChildSelection(child.id)}
                              >
                                <Checkbox
                                  checked={bulkForm.selected_children.includes(child.id)}
                                  onCheckedChange={() => toggleChildSelection(child.id)}
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{child.first_name} {child.last_name}</p>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    {ageInfo && <ageInfo.icon className={`w-3 h-3 ${ageInfo.color}`} />}
                                    {ageInfo?.label || ageGroup}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="space-y-2">
                    <Label>Preview ({bulkPreview.length} invoices)</Label>
                    <ScrollArea className="h-48 border rounded-lg">
                      {bulkPreview.length > 0 ? (
                        <div className="divide-y">
                          {bulkPreview.map((inv, idx) => (
                            <div key={idx} className="p-3 flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{inv.child_name}</p>
                                <p className="text-xs text-muted-foreground">{inv.description}</p>
                              </div>
                              <div className="text-right">
                                <Badge variant={inv.type === 'tuition' ? 'default' : 'secondary'} className="text-xs">
                                  {inv.type === 'tuition' ? 'Tuition' : 'Registration'}
                                </Badge>
                                <p className="font-bold text-green-600">${inv.amount.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-muted-foreground">
                          <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No invoices to preview</p>
                          <p className="text-xs mt-1">Configure fees in Settings to generate invoices</p>
                        </div>
                      )}
                    </ScrollArea>
                    
                    {bulkPreview.length > 0 && (
                      <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                        <span className="font-medium">Total Amount:</span>
                        <span className="text-xl font-bold text-green-600">
                          ${bulkPreview.reduce((sum, inv) => sum + inv.amount, 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleBulkInvoice} 
                      disabled={bulkProcessing || bulkPreview.length === 0 || !bulkForm.due_date}
                      data-testid="create-bulk-invoices-btn"
                    >
                      {bulkProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Create {bulkPreview.length} Invoices
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Single Invoice Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create Invoice</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateInvoice} className="space-y-4 mt-4">
                  {/* Invoice Type */}
                  <div className="space-y-2">
                    <Label>Invoice Type</Label>
                    <Select 
                      value={formData.invoice_type} 
                      onValueChange={handleInvoiceTypeChange}
                    >
                      <SelectTrigger data-testid="invoice-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tuition">
                          <span className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Tuition
                          </span>
                        </SelectItem>
                        <SelectItem value="registration">
                          <span className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Registration Fee
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Parent</Label>
                    <Select 
                      value={formData.parent_id} 
                      onValueChange={(value) => setFormData({...formData, parent_id: value})}
                    >
                      <SelectTrigger data-testid="invoice-parent-select">
                        <SelectValue placeholder="Select parent" />
                      </SelectTrigger>
                      <SelectContent>
                        {parents.map((parent) => (
                          <SelectItem key={parent.id} value={parent.id}>
                            {parent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Child</Label>
                    <Select 
                      value={formData.child_id} 
                      onValueChange={handleChildSelect}
                    >
                      <SelectTrigger data-testid="invoice-child-select">
                        <SelectValue placeholder="Select child" />
                      </SelectTrigger>
                      <SelectContent>
                        {children.filter(c => !formData.parent_id || c.parent_id === formData.parent_id).map((child) => (
                          <SelectItem key={child.id} value={child.id}>
                            {child.first_name} {child.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Auto-populated fee info */}
                  {selectedChildInfo && feesSettings && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                        <div className="text-sm">
                          {selectedChildInfo.ageGroupInfo && (
                            <div className="flex items-center gap-2 mb-1">
                              <selectedChildInfo.ageGroupInfo.icon className={`w-4 h-4 ${selectedChildInfo.ageGroupInfo.color}`} />
                              <span className="font-medium">{selectedChildInfo.ageGroupInfo.label}</span>
                            </div>
                          )}
                          {formData.invoice_type === 'tuition' && feesSettings[selectedChildInfo.ageGroup] ? (
                            <p className="text-blue-700">
                              {feesSettings[selectedChildInfo.ageGroup].rate_type === 'monthly' ? 'Monthly' : 'Weekly'} rate: 
                              <span className="font-bold"> ${feesSettings[selectedChildInfo.ageGroup].amount || '0'}</span>
                            </p>
                          ) : formData.invoice_type === 'registration' && feesSettings.registration ? (
                            <p className="text-blue-700">
                              Registration fee ({feesSettings.registration.frequency === 'biannual' ? 'Bi-Annual' : 'Yearly'}): 
                              <span className="font-bold"> ${feesSettings.registration.amount || '0'}</span>
                            </p>
                          ) : (
                            <p className="text-amber-600">No fee configured for this category</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        required
                        data-testid="invoice-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                        required
                        data-testid="invoice-due-date-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="e.g., Weekly Child Care - Week of Jan 1"
                      required
                      data-testid="invoice-description-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => {
                      setDialogOpen(false);
                      setSelectedChildInfo(null);
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-invoice-btn">
                      Create Invoice
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Invoices List */}
        <Card className="card-clay">
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="font-heading flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                {user.role === 'parent' ? 'My Invoices' : 'All Invoices'}
              </CardTitle>
              
              {/* Search and Filter - Admin only */}
              {user.role !== 'parent' && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search invoices..."
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      className="pl-9 w-full sm:w-64"
                      data-testid="invoice-search"
                    />
                  </div>
                  <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                    <SelectTrigger className="w-36" data-testid="invoice-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {user.role !== 'parent' && (
              <p className="text-sm text-muted-foreground mb-4">
                Showing {filteredInvoices.length} of {invoices.length} invoices
              </p>
            )}
            
            {(user.role === 'parent' ? invoices : filteredInvoices).length > 0 ? (
              <div className="space-y-3">
                {(user.role === 'parent' ? invoices : filteredInvoices).map((invoice) => (
                  <div 
                    key={invoice.id}
                    className={cn(
                      "flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 rounded-xl border gap-4",
                      invoice.status === 'paid' ? "bg-green-50 border-green-200" : 
                      invoice.status === 'partial' ? "bg-blue-50 border-blue-200" :
                      invoice.status === 'overdue' ? "bg-red-50 border-red-200" :
                      "bg-muted/50 border-transparent"
                    )}
                    data-testid={`invoice-${invoice.id}`}
                  >
                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <p className="font-semibold truncate">{invoice.description}</p>
                        {getStatusBadge(invoice.status)}
                        {invoice.invoice_type === 'late_fee' && (
                          <Badge variant="outline" className="text-red-600 border-red-300 text-xs">
                            Late Fee
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {invoice.child_name} • Due: {invoice.due_date}
                        {invoice.parent_email && user.role !== 'parent' && (
                          <span className="ml-2 text-xs">({invoice.parent_email})</span>
                        )}
                      </p>
                      {invoice.amount_paid > 0 && invoice.status !== 'paid' && (
                        <p className="text-xs text-green-600 mt-1">
                          ${invoice.amount_paid?.toFixed(2)} paid • ${(invoice.amount - (invoice.amount_paid || 0)).toFixed(2)} remaining
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-xl">${invoice.amount?.toFixed(2)}</p>
                        {invoice.amount_paid > 0 && (
                          <p className="text-xs text-muted-foreground">Total</p>
                        )}
                      </div>
                      
                      {/* Parent Pay Button */}
                      {invoice.status !== 'paid' && user.role === 'parent' && (
                        <Button
                          onClick={() => handlePayment(invoice)}
                          disabled={processing === invoice.id}
                          data-testid={`pay-invoice-${invoice.id}`}
                        >
                          {processing === invoice.id ? (
                            'Processing...'
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Pay Now
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Admin Edit/Delete Buttons */}
                      {user.role !== 'parent' && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openEditInvoice(invoice)}
                            title="Edit invoice"
                            data-testid={`edit-invoice-${invoice.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            title="Delete invoice"
                            data-testid={`delete-invoice-${invoice.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{invoiceSearch || invoiceStatusFilter !== 'all' ? 'No invoices match your search' : 'No invoices yet'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Invoice Dialog */}
        <Dialog open={editInvoiceDialog} onOpenChange={setEditInvoiceDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="edit-invoice-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-primary" />
                Edit Invoice
              </DialogTitle>
            </DialogHeader>
            
            {editingInvoice && (
              <div className="space-y-4 py-4">
                {/* Invoice ID */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                  <Receipt className="w-4 h-4" />
                  <span>Invoice ID: {editingInvoice.id?.slice(0, 8)}...</span>
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Input
                    id="edit-description"
                    value={editingInvoice.description || ''}
                    onChange={(e) => setEditingInvoice({...editingInvoice, description: e.target.value})}
                    data-testid="edit-invoice-description"
                  />
                </div>
                
                {/* Amount and Amount Paid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-amount">Total Amount ($)</Label>
                    <Input
                      id="edit-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingInvoice.amount}
                      onChange={(e) => setEditingInvoice({...editingInvoice, amount: e.target.value})}
                      data-testid="edit-invoice-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-amount-paid">Amount Paid ($)</Label>
                    <Input
                      id="edit-amount-paid"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingInvoice.amount_paid}
                      onChange={(e) => setEditingInvoice({...editingInvoice, amount_paid: e.target.value})}
                      data-testid="edit-invoice-amount-paid"
                    />
                  </div>
                </div>
                
                {/* Balance Display */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Balance Due:</span>
                    <span className={cn(
                      "font-bold text-lg",
                      (parseFloat(editingInvoice.amount) - parseFloat(editingInvoice.amount_paid || 0)) <= 0 
                        ? "text-green-600" 
                        : "text-amber-700"
                    )}>
                      ${Math.max(0, (parseFloat(editingInvoice.amount) || 0) - (parseFloat(editingInvoice.amount_paid) || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Due Date and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-due-date">Due Date</Label>
                    <Input
                      id="edit-due-date"
                      type="date"
                      value={editingInvoice.due_date || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, due_date: e.target.value})}
                      data-testid="edit-invoice-due-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select 
                      value={editingInvoice.status || 'pending'} 
                      onValueChange={(value) => setEditingInvoice({...editingInvoice, status: value})}
                    >
                      <SelectTrigger data-testid="edit-invoice-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Child and Parent Info (read-only) */}
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  <p><strong>Child:</strong> {editingInvoice.child_name}</p>
                  {editingInvoice.parent_email && (
                    <p><strong>Parent:</strong> {editingInvoice.parent_email}</p>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setEditInvoiceDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveInvoice}
                    disabled={savingInvoice}
                    data-testid="save-invoice-btn"
                  >
                    {savingInvoice ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* Billing Worksheet Tab - Admin/Provider only */}
          {user.role !== 'parent' && (
            <TabsContent value="worksheet">
              <BillingWorksheet />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;
